<template>
    <VJoyDialog ref="purchaseOrderDialog" open class="po-dialog" :cancel-text="t('generic.form.cancel.label')" @dialog:hide="emits('close:dialog')">
        <template #dialog-header>
            {{ t('projectclient.projectlist.dialog.purchaseorder.modify') }}
        </template>
        <template #dialog-subheader>{{ t('projectclient.projectlist.dialog.purchaseorder.title', {0: data.title}) }}</template>
        <template #dialog-body>
            <form :id="formId">
                <div class="po-dialog__input-wrapper">
                    <!-- Purchase order number -->
                    <div class="po-dialog__input-wrapper__left">
                        <VJoyInput
                            v-model="r$.purchaseOrderNumber.$value"
                            :label="t('projectclient.projectlist.dialog.purchaseorder.label.number')"
                            :placeholder="t('projectclient.projectlist.dialog.purchaseorder.label.number')"
                            name="po-number"
                            :invalid="r$.purchaseOrderNumber.$error"
                        />
                        <ValidationNotification
                            v-if="isRegexFaulty && purchaseOrderNumber"
                            :label="t('projectclient.projectlist.dialog.purchaseorder.format.notification')"
                            type="neutral"
                        />
                        <ValidationNotification
                            v-if="doesPoAlreadyExist"
                            :label="t('projectclient.projectlist.dialog.purchaseorder.existing.notification')"
                            type="info"
                        />
                        <VJoyFormError v-if="r$.purchaseOrderNumber.$error" :errors="r$.purchaseOrderNumber.$errors" />
                    </div>
                    <!-- Total amount -->
                    <div class="po-dialog__input-wrapper__right">
                        <MaltInputMask
                            v-model="purchaseOrderAmount"
                            :label="t('projectclient.projectlist.dialog.purchaseorder.label.total')"
                            :placeholder="t('projectclient.projectlist.dialog.purchaseorder.label.total')"
                            name="total-amount"
                            :unit="displayedCurrency"
                            preset="price"
                            :invalid="r$.purchaseOrderAmount.$error"
                        />
                        <VJoyFormError v-if="r$.purchaseOrderAmount.$error" :errors="r$.purchaseOrderAmount.$errors" />
                        <ValidationNotification
                            v-if="doesPoAlreadyExist"
                            :label="t('projectclient.projectlist.dialog.purchaseorder.total.notification')"
                            type="info"
                        />
                        <ValidationNotification
                            v-else
                            :label="t('projectclient.projectlist.dialog.purchaseorder.label.total.autofilled')"
                            type="neutral"
                        />
                    </div>
                </div>
                <VJoyTextarea
                    v-model="purchaseOrderComment"
                    :label="t('projectclient.projectlist.dialog.purchaseorder.label.comment')"
                    :placeholder="t('projectclient.projectlist.dialog.purchaseorder.label.comment')"
                    optional-label="optional"
                />
            </form>
        </template>
        <template #dialog-confirm>
            <VJoySubmit :loading="isPendingUpdate" :action="confirmNewPoNumber" size="small">
                {{ t('projectclient.projectlist.dialog.purchaseorder.modify') }}
            </VJoySubmit>
        </template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import {ref, watch, computed, onMounted, useTemplateRef} from 'vue';
    import {useTrackPurchaseOrder} from '@common/composables';
    import {useUserStore} from '@common/stores';
    import {ValidationNotification} from '@common/ui';
    import {maxValue, minValue, required, withMessage} from '@regle/rules';
    import debounce from 'lodash-es/debounce';
    import {storeToRefs} from 'pinia';
    import {VJoySubmit, MaltInputMask} from '#components';
    import {useTranslation} from '#imports';
    import {useRegle, validateForm} from '@malt/forms';
    import {VJoyDialog, VJoyFormError, VJoyInput, VJoyTextarea} from '@maltjoy/core-vue';
    import type {UpsertProjectPurchaseOrderRequest, UpsertQuotePurchaseOrderRequest} from '@api';
    import {useProjectsListStore} from '../../stores';
    import {ProjectCurrencyTypeMapper} from '../../types';
    import type {FormattedClientProjectResource} from '../../types';

    const props = defineProps<{
        visible: boolean;
        data: FormattedClientProjectResource;
    }>();

    const emits = defineEmits<{
        'close:dialog': [];
    }>();

    const {t} = useTranslation();
    const userStore = useUserStore();
    const {roles} = storeToRefs(userStore);

    const projectListStore = useProjectsListStore();

    const {resolvePurchaseOrderChange, trackPurchaseOrderAddedOrUpdated} = useTrackPurchaseOrder();

    const displayedCurrency = computed(() => {
        return ProjectCurrencyTypeMapper[props.data.currency ?? 'EUR'];
    });

    // handle dialog opening / closing
    const purchaseOrderDialogRef = useTemplateRef('purchaseOrderDialog');

    watch(
        () => props.visible,
        async (value) => {
            if (value) {
                await purchaseOrderDialogRef.value?.show();
            } else {
                await purchaseOrderDialogRef.value?.hide();
            }
        },
    );

    // handle purchase order input updates
    const doesPoAlreadyExist = ref(false);

    const purchaseOrder = ref<undefined | string>('');
    const purchaseOrderNumber = ref(props.data.purchaseOrderNumber || '');
    const purchaseOrderAmount = ref<number | undefined>(Number(props.data.total));
    const purchaseOrderComment = ref(props.data.purchaseOrderComment);

    const formId = 'purchase-order-dialog';

    const {r$} = useRegle(
        {purchaseOrderNumber, purchaseOrderAmount},
        {
            purchaseOrderNumber: {
                required: withMessage(required, () => t('projectclient.billinginformation.purchaseorder.total.error')),
            },
            purchaseOrderAmount: {minValue: minValue(0), maxValue: maxValue(1_000_000_000)},
        },
    );

    async function checkIfPoAlreadyRegistered() {
        if (!purchaseOrderNumber.value) doesPoAlreadyExist.value = false;
        if (!purchaseOrderNumber.value || !props.data.companyId) return;
        const result = await projectListStore.checkIfPurchaseOrderExists({
            companyId: props.data.companyId,
            purchaseOrderNumber: purchaseOrderNumber.value,
        });
        doesPoAlreadyExist.value = !!result;
        if (result?.budget) {
            purchaseOrderAmount.value = result.budget;
        }
        purchaseOrder.value = result?.id;
    }

    // initialise id in case existing po is updated with new budget / comment
    onMounted(() => {
        if (purchaseOrderNumber.value) checkIfPoAlreadyRegistered();
    });

    const isRegexFaulty = ref(false);
    const regex = /^(?=.*\d)[\w-]{1,25}$/;

    const debouncedcheckIfPoAlreadyRegistered = debounce(() => {
        checkIfPoAlreadyRegistered();
    }, 1000);

    watch(
        () => purchaseOrderNumber.value,
        () => {
            // check if format is compliant
            if (purchaseOrderNumber.value) {
                isRegexFaulty.value = !regex.test(purchaseOrderNumber.value);
            }
            // check if this company already has this identifier in use
            debouncedcheckIfPoAlreadyRegistered();
        },
    );

    // handle purchase order confirmation
    const hasAuthorityToChangePoNumber = computed(() => roles.value.includes('ROLE_PO_MANAGER'));

    const isPendingUpdate = ref(false);

    function createUpsertRequest(
        projectType: 'PROJECT_PROPOSAL' | 'COMPLETED_PROJECT',
    ): UpsertProjectPurchaseOrderRequest | UpsertQuotePurchaseOrderRequest {
        const purchaseOrderUpsertRequest = {
            id: purchaseOrder.value,
            identifier: purchaseOrderNumber.value,
            budget: purchaseOrderAmount.value,
            comment: purchaseOrderComment.value,
        };

        let requestObject;

        switch (projectType) {
            case 'PROJECT_PROPOSAL': {
                requestObject = {
                    quoteId: props.data.id,
                    purchaseOrderUpsertRequest,
                };
                break;
            }
            case 'COMPLETED_PROJECT': {
                requestObject = {
                    projectId: props.data.id,
                    purchaseOrderUpsertRequest,
                };
                break;
            }
        }
        return requestObject;
    }

    async function confirmNewPoNumber() {
        const {valid} = await validateForm(r$, {formId});
        if (!hasAuthorityToChangePoNumber.value || !purchaseOrderNumber.value || !valid) return;

        isPendingUpdate.value = true;

        const purchaseOrderChange = resolvePurchaseOrderChange(props.data.purchaseOrderNumber, purchaseOrderNumber.value);
        const result = await projectListStore.updatePurchaseOrder(createUpsertRequest(props.data.type), {
            projectName: props.data.title,
            projectType: props.data.type,
        });
        if (result === 'applied') {
            await trackPurchaseOrderAddedOrUpdated(purchaseOrderChange, 'client_project_list', props.data.purchaseOrderMandatory);
        }

        setTimeout(async () => {
            purchaseOrderDialogRef.value?.hide();
            isPendingUpdate.value = false;
            await projectListStore.fetchProjectList();
        }, 1000);
    }
</script>

<style lang="scss" scoped>
    .po-dialog {
        &__input-wrapper {
            display: flex;
            justify-content: space-between;
            flex-wrap: nowrap;
            gap: var(--joy-core-spacing-6);

            &__left,
            &__right {
                flex-grow: 1;
                max-width: 50%;
            }

            &__right {
                &__info {
                    margin: var(--joy-core-spacing-2) 0 var(--joy-core-spacing-6) 0;
                    display: flex;
                    align-items: center;
                    color: var(--joy-color-neutral-40);

                    p {
                        margin-left: var(--joy-core-spacing-2);
                    }
                }
            }
        }
    }
</style>
